import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import Modal from '../Modal/Modal'
import CharacterModal from '../CharacterModal/CharacterModal'
import API from '../../services/apiService'
import { downloadInstaller, showDownloadError, showDownloadSuccess } from '../../services/downloadService'
import { 
  Skeleton, 
  SkeletonAvatar, 
  AccountInfoSkeleton, 
  CharacterListSkeleton 
} from '../Skeleton/Skeleton'
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay'
import './AccountModal.css'
import { downloadPatch } from '../../services/downloadService'

/**
 * Leet-speak character map for nickname normalization
 */
const LEET_MAP = {
  '4': 'a', '@': 'a', 'á': 'a', 'à': 'a', 'â': 'a', 'ã': 'a',
  '3': 'e', 'é': 'e', 'è': 'e', 'ê': 'e',
  '1': 'i', '!': 'i', '|': 'i', 'í': 'i', 'ì': 'i', 'î': 'i',
  '0': 'o', 'ó': 'o', 'ò': 'o', 'ô': 'o', 'õ': 'o',
  '5': 's', '$': 's',
  '7': 't',
  '6': 'g', '9': 'g',
  '2': 'z',
  '8': 'b',
  'ú': 'u', 'ù': 'u', 'û': 'u',
  'ç': 'c',
}

function deepNormalize(str) {
  return str
    .toLowerCase()
    .replace(/[\s\-_.]+/g, '')
    .split('')
    .map(c => LEET_MAP[c] || c)
    .join('')
}

const FORBIDDEN_WORDS = [
  'admin', 'adm', 'administrator', 'administrador',
  'mod', 'moderator', 'moderador',
  'staff', 'suporte', 'support',
  'owner', 'dono', 'proprietario',
  'dev', 'developer', 'desenvolvedor',
  'oficial', 'official',
  'gm', 'gamemaster', 'mestre',
  'dungeon', 'master', 'dm',
  'system', 'sistema', 'bot',
  'server', 'servidor',
  'root', 'superuser',
  'helper', 'ajudante',
  'manager', 'gerente',
  'supervisor', 'coordenador',
  'director', 'diretor',
  'team', 'equipe', 'crew',
]

const PROFANITY_WORDS = [
  'caralho', 'porra', 'merda', 'puta', 'putaria',
  'buceta', 'boceta', 'xota', 'xoxota', 'xereca',
  'piroca', 'pica', 'piru', 'rola', 'pau',
  'punheta', 'siririca', 'gozar',
  'cuzão', 'cuzao', 'arrombado', 'babaca',
  'vagabundo', 'vagabunda', 'viado', 'veado', 'bicha',
  'boiola', 'baitola', 'cagão', 'cagao', 'cagar',
  'nojento', 'tarado', 'tarada', 'sacanagem',
  'pentelho', 'perereca', 'broxar',
  'foda', 'fodao', 'fudeu', 'fuder',
  'cacete', 'escroto',
  'otario', 'otário', 'imbecil',
  'idiota', 'trouxa',
  'desgraca', 'desgraça', 'desgracado',
  'fuck', 'fucking', 'fucker', 'motherfucker',
  'shit', 'bitch', 'asshole', 'dick', 'cock',
  'cunt', 'whore', 'slut', 'bastard',
  'nigger', 'nigga', 'faggot', 'fag',
  'retard', 'retarded',
]

function containsForbiddenWord(nickname) {
  const normalized = deepNormalize(nickname)
  for (const word of FORBIDDEN_WORDS) {
    if (normalized.includes(word)) return true
  }
  return false
}

function containsProfanity(nickname) {
  const normalized = deepNormalize(nickname)
  for (const word of PROFANITY_WORDS) {
    if (normalized.includes(word)) return true
  }
  return false
}

function isGibberish(nickname) {
  const stripped = nickname.replace(/[\s_-]/g, '')
  if (stripped.length < 4) return false
  if (/(.)\1{3,}/.test(stripped)) return true

  if (stripped.length >= 6) {
    const pairs = []
    for (let i = 0; i < stripped.length - 1; i += 2) {
      pairs.push(stripped.substring(i, i + 2))
    }
    if (pairs.length >= 3) {
      const uniquePairs = new Set(pairs)
      if (uniquePairs.size <= 2) return true
    }
  }

  const keyboardPatterns = [
    /qwerty|asdfgh|zxcvbn/i,
    /qwertz|asdfgh|yxcvbn/i,
    /[-=]{3,}|[uiop]{3,}|[jklö]{3,}/i,
  ]

  for (const pattern of keyboardPatterns) {
    if (pattern.test(stripped)) return true
  }

  return false
}

/**
 * Client-side nickname validation that mirrors backend logic
 * Returns error string or null if valid
 */
function validateNicknameLocal(nickname, isRequired = false) {
  const trimmed = (nickname || '').trim()

  if (!trimmed) {
    return isRequired ? 'Nickname cannot be empty' : null
  }

  if (trimmed.length > 30) {
    return 'Nickname must be at most 30 characters'
  }

  if (trimmed.length < 3) {
    return 'Nickname must be at least 3 characters'
  }

  if (trimmed.startsWith('[]') || trimmed.startsWith('[') || trimmed.endsWith(']')) {
    return 'Nickname cannot use square brackets at the beginning or end'
  }

  if (/\s{2,}/.test(trimmed) || /[\t\n\r\v\f]/.test(trimmed)) {
    return 'Nickname cannot contain multiple consecutive spaces or special whitespace'
  }

  if (!/^[a-zA-Z0-9_ -]+$/.test(trimmed)) {
    return 'Nickname can only contain letters, numbers, spaces, hyphens and underscores'
  }

  const forbiddenPrefixes = ['ADM-', 'GM-', 'MOD-', 'DEV-', 'STAFF-', '[', 'TEAM-']
  for (const prefix of forbiddenPrefixes) {
    if (trimmed.toUpperCase().startsWith(prefix)) {
      return `Nickname cannot start with "${prefix}"`
    }
  }

  if (containsForbiddenWord(trimmed)) {
    return 'This nickname contains forbidden words or patterns'
  }

  if (containsProfanity(trimmed)) {
    return 'Nickname cannot contain inappropriate language'
  }

  if (isGibberish(trimmed)) {
    return 'Nickname contains repetitive or invalid patterns'
  }

  const uppercaseCount = (trimmed.match(/[A-Z]/g) || []).length
  const uppercaseRatio = uppercaseCount / trimmed.replace(/\s/g, '').length
  if (uppercaseCount > 5 && uppercaseRatio > 0.7) {
    return 'Nickname cannot be mostly uppercase'
  }

  if (trimmed.toLowerCase().includes('system') ||
      trimmed.toLowerCase().includes('server') ||
      trimmed.toLowerCase().includes('automatic')) {
    return 'Nickname cannot contain system-related words'
  }

  return null
}

// Bot Status Icon for account tab - Chocolate Medieval Design
const BotTabIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="account__info-icon">
    <defs>
      <linearGradient id="botAccGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e6c84a" />
        <stop offset="40%" stopColor="#8b7355" />
        <stop offset="100%" stopColor="#5c4a3a" />
      </linearGradient>
      <linearGradient id="botAccBody" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#6b5949" />
        <stop offset="100%" stopColor="#4a3829" />
      </linearGradient>
      <linearGradient id="botAccEyes" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#22c55e" />
        <stop offset="100%" stopColor="#4ade80" />
      </linearGradient>
      <filter id="botAccGlow">
        <feGaussianBlur stdDeviation="1" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="1.3"/>
        </feComponentTransfer>
      </filter>
    </defs>
    
    <rect x="5" y="8" width="14" height="11" rx="2.5" fill="url(#botAccBody)" stroke="#e6c84a" strokeWidth="0.8" />
    
    <rect x="2" y="11" width="3" height="6" rx="1.5" fill="url(#botAccBody)" stroke="#8b7355" strokeWidth="0.6" />
    <rect x="19" y="11" width="3" height="6" rx="1.5" fill="url(#botAccBody)" stroke="#8b7355" strokeWidth="0.6" />
    
    <ellipse cx="9" cy="12.5" rx="1.8" ry="2" fill="url(#botAccEyes)" filter="url(#botAccGlow)" opacity="0.9" />
    <ellipse cx="15" cy="12.5" rx="1.8" ry="2" fill="url(#botAccEyes)" filter="url(#botAccGlow)" opacity="0.9" />
    <circle cx="9.5" cy="12" r="0.6" fill="#dcfce7" />
    <circle cx="15.5" cy="12" r="0.6" fill="#dcfce7" />
    
    <rect x="9.5" y="15.5" width="5" height="2.5" rx="0.5" fill="rgba(230, 200, 74, 0.25)" stroke="#e6c84a" strokeWidth="0.5" />
    
    <rect x="10.5" y="3.5" width="3" height="4.5" rx="1.5" fill="url(#botAccGrad)" stroke="#8b7355" strokeWidth="0.7" />
    <circle cx="12" cy="2.5" r="1.3" fill="#e6c84a" stroke="#8b7355" strokeWidth="0.6" />
    <circle cx="12" cy="2.5" r="0.5" fill="#22c55e" filter="url(#botAccGlow)" />
    
    <rect x="7.5" y="19" width="2.5" height="3" rx="1" fill="url(#botAccBody)" stroke="#8b7355" strokeWidth="0.6" />
    <rect x="14" y="19" width="2.5" height="3" rx="1" fill="url(#botAccBody)" stroke="#8b7355" strokeWidth="0.6" />
  </svg>
)

// Support Icon for support tab - Medieval Emblem
const SupportIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="supGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <linearGradient id="supInner" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#faf6f0" stopOpacity="0.9"/>
        <stop offset="100%" stopColor="#e8dcc4" stopOpacity="0.7"/>
      </linearGradient>
    </defs>
    {/* Shield outline */}
    <path d="M12 2L3 7v6c0 5.25 9 9 9 9s9-3.75 9-9V7L12 2z" fill="none" stroke="url(#supGold)" strokeWidth="1.8" strokeLinejoin="round"/>
    {/* Shield inner */}
    <path d="M12 4.5L5.5 8.3v4.7c0 4 6.5 7 6.5 7s6.5-3 6.5-7V8.3L12 4.5z" fill="url(#supInner)" stroke="#8b6914" strokeWidth="0.4"/>
    {/* Cross emblem */}
    <path d="M12 8v8M8 12h8" stroke="url(#supGold)" strokeWidth="2" strokeLinecap="round"/>
    {/* Decorative dots */}
    <circle cx="12" cy="3.5" r="1" fill="url(#supGold)"/>
    <circle cx="12" cy="22" r="1" fill="url(#supGold)"/>
    {/* Side decorations */}
    <circle cx="3" cy="7" r="1" fill="#5a4510" opacity="0.5"/>
    <circle cx="21" cy="7" r="1" fill="#5a4510" opacity="0.5"/>
  </svg>
)

// Gears Icon - Bot Config
const GearsIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="gearMain" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fcd34d"/>
        <stop offset="40%" stopColor="#e6c84a"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <linearGradient id="gearDark" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d97706"/>
        <stop offset="100%" stopColor="#78350f"/>
      </linearGradient>
      <linearGradient id="gearInner" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fbbf24"/>
        <stop offset="100%" stopColor="#92400e"/>
      </linearGradient>
      <filter id="gearShadow">
        <feDropShadow dx="1" dy="1" stdDeviation="1" floodColor="#78350f" floodOpacity="0.4"/>
      </filter>
    </defs>
    <g filter="url(#gearShadow)">
      <path d="M10 3.5l.5-1.5h-1l.5 1.5" fill="url(#gearMain)"/>
      <path d="M14 3.5l.5-1.5h-1l.5 1.5" fill="url(#gearMain)"/>
      <path d="M6.5 6.5l-1.5-.5v1l1.5-.5" fill="url(#gearMain)"/>
      <path d="M17.5 6.5l1.5-.5v1l-1.5-.5" fill="url(#gearMain)"/>
      <path d="M3.5 10l-1.5.5v1l1.5.5" fill="url(#gearMain)"/>
      <path d="M20.5 10l1.5.5v1l-1.5.5" fill="url(#gearMain)"/>
      <path d="M6.5 17.5l-1.5.5v-1l1.5.5" fill="url(#gearMain)"/>
      <path d="M17.5 17.5l1.5.5v-1l-1.5.5" fill="url(#gearMain)"/>
      <path d="M10 20.5l.5 1.5h-1l.5-1.5" fill="url(#gearMain)"/>
      <path d="M14 20.5l.5 1.5h-1l.5-1.5" fill="url(#gearMain)"/>
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="url(#gearMain)" strokeWidth="2.5"/>
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="url(#gearDark)" strokeWidth="1.2" opacity="0.4"/>
      <circle cx="12" cy="12" r="3" fill="url(#gearInner)" stroke="#78350f" strokeWidth="0.6"/>
      <circle cx="12" cy="12" r="1.2" fill="#fef3c7" opacity="0.6"/>
    </g>
    <g filter="url(#gearShadow)" opacity="0.75">
      <path d="M18 16.5l.3-1.2h-.6l.3 1.2" fill="url(#gearMain)"/>
      <path d="M20.5 18l.3-1.2h-.6l.3 1.2" fill="url(#gearMain)"/>
      <path d="M16.5 14l-1.2-.3v.6l1.2-.3" fill="url(#gearMain)"/>
      <path d="M20.5 15l1.2-.3v.6l-1.2-.3" fill="url(#gearMain)"/>
      <path d="M16 18.5l-1.2.3v-.6l1.2.3" fill="url(#gearMain)"/>
      <path d="M20 20l1.2.3v-.6l-1.2.3" fill="url(#gearMain)"/>
      <circle cx="18.5" cy="17.5" r="2.8" fill="none" stroke="url(#gearMain)" strokeWidth="2"/>
      <circle cx="18.5" cy="17.5" r="1.5" fill="url(#gearInner)" stroke="#78350f" strokeWidth="0.4"/>
      <circle cx="18.5" cy="17.5" r="0.6" fill="#fef3c7" opacity="0.5"/>
    </g>
  </svg>
)

// Robot Avatar Icon for account
const KnightAvatarIcon = () => (
  <img 
    src="/assets/robot.png" 
    alt="Robot Avatar"
    className="account__avatar-icon"
    style={{width: '56px', height: '56px'}}
  />
)

// Logout Icon
const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__btn-icon">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// Tab Icon - Medieval Window/Portal
const TabIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="tabGoldPremium" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <linearGradient id="tabGlass" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e8f4f8" stopOpacity="0.9"/>
        <stop offset="100%" stopColor="#b8d4e3" stopOpacity="0.7"/>
      </linearGradient>
    </defs>
    {/* Frame */}
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="url(#tabGoldPremium)" strokeWidth="2"/>
    {/* Top bar */}
    <rect x="3" y="3" width="18" height="5" rx="2" fill="url(#tabGoldPremium)"/>
    {/* Window panes */}
    <rect x="5" y="10" width="6" height="9" rx="1" fill="url(#tabGlass)" stroke="#8b6914" strokeWidth="0.5"/>
    <rect x="13" y="10" width="6" height="9" rx="1" fill="url(#tabGlass)" stroke="#8b6914" strokeWidth="0.5"/>
    {/* Decorative dots on top */}
    <circle cx="7" cy="5.5" r="1" fill="#5a4510"/>
    <circle cx="12" cy="5.5" r="1" fill="#5a4510"/>
    <circle cx="17" cy="5.5" r="1" fill="#5a4510"/>
  </svg>
)

// License Icon - Ornate Key
const LicenseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="keyGoldPremium" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    {/* Key head - ornate */}
    <circle cx="7" cy="7" r="5" fill="none" stroke="url(#keyGoldPremium)" strokeWidth="2"/>
    <circle cx="7" cy="7" r="2.5" fill="url(#keyGoldPremium)"/>
    {/* Key shaft */}
    <path d="M11 11 L20 20" stroke="url(#keyGoldPremium)" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Key teeth */}
    <path d="M16 16 L18 14" stroke="url(#keyGoldPremium)" strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 18 L20 16" stroke="url(#keyGoldPremium)" strokeWidth="2" strokeLinecap="round"/>
    {/* Decorative ring */}
    <circle cx="7" cy="7" r="3.5" fill="none" stroke="#5a4510" strokeWidth="0.5" strokeDasharray="1.5 1"/>
  </svg>
)

// Calendar Icon - Medieval style
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="calGoldPremium" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <linearGradient id="calPaper" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#faf6f0"/>
        <stop offset="100%" stopColor="#e8dcc4"/>
      </linearGradient>
    </defs>
    {/* Calendar body */}
    <rect x="3" y="5" width="18" height="16" rx="2" fill="url(#calPaper)" stroke="url(#calGoldPremium)" strokeWidth="1.5"/>
    {/* Header */}
    <rect x="3" y="5" width="18" height="5" rx="2" fill="url(#calGoldPremium)"/>
    {/* Rings */}
    <rect x="7" y="2" width="2" height="5" rx="1" fill="#6b5340"/>
    <rect x="15" y="2" width="2" height="5" rx="1" fill="#6b5340"/>
    {/* Date grid hint */}
    <circle cx="8" cy="14" r="1.5" fill="#c9a227"/>
    <circle cx="12" cy="14" r="1.5" fill="#8b6914" opacity="0.4"/>
    <circle cx="16" cy="14" r="1.5" fill="#8b6914" opacity="0.4"/>
    <circle cx="8" cy="18" r="1.5" fill="#8b6914" opacity="0.4"/>
    <circle cx="12" cy="18" r="1.5" fill="#8b6914" opacity="0.4"/>
  </svg>
)

// Crown Icon - For premium packages
const CrownIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="account__crown-icon">
    <defs>
      <linearGradient id="crownGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="30%" stopColor="#ffec8b"/>
        <stop offset="50%" stopColor="#ffd700"/>
        <stop offset="70%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <filter id="crownGlow">
        <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="#ffd700" floodOpacity="0.5"/>
      </filter>
    </defs>
    {/* Crown base */}
    <path d="M4 18 L4 10 L8 14 L12 8 L16 14 L20 10 L20 18 Z" fill="url(#crownGold)" stroke="#8b6914" strokeWidth="0.5" filter="url(#crownGlow)"/>
    {/* Crown band */}
    <rect x="4" y="17" width="16" height="3" rx="1" fill="url(#crownGold)" stroke="#6b4f12" strokeWidth="0.5"/>
    {/* Jewels */}
    <circle cx="8" cy="18.5" r="1" fill="#e74c3c"/>
    <circle cx="12" cy="18.5" r="1.2" fill="#3498db"/>
    <circle cx="16" cy="18.5" r="1" fill="#2ecc71"/>
    {/* Top jewels */}
    <circle cx="12" cy="9.5" r="1.5" fill="#9b59b6"/>
    <circle cx="8" cy="13" r="0.8" fill="#e74c3c"/>
    <circle cx="16" cy="13" r="0.8" fill="#2ecc71"/>
  </svg>
)

// Shield Icon - For security/protection
const ShieldIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="account__shield-icon">
    <defs>
      <linearGradient id="shieldMetal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c0c0c0"/>
        <stop offset="30%" stopColor="#e8e8e8"/>
        <stop offset="50%" stopColor="#d4d4d4"/>
        <stop offset="70%" stopColor="#a0a0a0"/>
        <stop offset="100%" stopColor="#808080"/>
      </linearGradient>
      <linearGradient id="shieldGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    {/* Shield body */}
    <path d="M12 2 L21 6 L21 12 C21 17 17 21 12 22 C7 21 3 17 3 12 L3 6 Z" fill="url(#shieldMetal)" stroke="url(#shieldGold)" strokeWidth="1.5"/>
    {/* Cross decoration */}
    <rect x="10.5" y="6" width="3" height="12" rx="0.5" fill="url(#shieldGold)"/>
    <rect x="6" y="10" width="12" height="3" rx="0.5" fill="url(#shieldGold)"/>
    {/* Center gem */}
    <circle cx="12" cy="11.5" r="2" fill="#c9a227" stroke="#8b6914" strokeWidth="0.5"/>
  </svg>
)

// Sword Icon - For power/strength
const SwordIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="account__sword-icon">
    <defs>
      <linearGradient id="bladeMetal" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#a0a0a0"/>
        <stop offset="50%" stopColor="#e8e8e8"/>
        <stop offset="100%" stopColor="#a0a0a0"/>
      </linearGradient>
      <linearGradient id="swordGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    {/* Blade */}
    <path d="M12 2 L14 3 L14 14 L12 15 L10 14 L10 3 Z" fill="url(#bladeMetal)" stroke="#606060" strokeWidth="0.5"/>
    {/* Guard */}
    <rect x="7" y="14" width="10" height="2" rx="1" fill="url(#swordGold)" stroke="#6b4f12" strokeWidth="0.3"/>
    {/* Handle */}
    <rect x="10.5" y="16" width="3" height="5" rx="0.5" fill="#4a2f17" stroke="#3d2518" strokeWidth="0.3"/>
    {/* Pommel */}
    <circle cx="12" cy="22" r="1.5" fill="url(#swordGold)" stroke="#6b4f12" strokeWidth="0.3"/>
    {/* Blade shine */}
    <line x1="12" y1="4" x2="12" y2="12" stroke="#ffffff" strokeWidth="0.5" opacity="0.6"/>
  </svg>
)

// Email Icon - Sealed Letter
const EmailIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="emailPaper" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#faf6f0"/>
        <stop offset="100%" stopColor="#e8dcc4"/>
      </linearGradient>
      <linearGradient id="emailSeal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    {/* Envelope body */}
    <rect x="2" y="5" width="20" height="14" rx="2" fill="url(#emailPaper)" stroke="#8b7355" strokeWidth="1"/>
    {/* Flap */}
    <path d="M2 5 L12 12 L22 5" fill="none" stroke="#8b7355" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Wax seal */}
    <circle cx="12" cy="14" r="3" fill="url(#emailSeal)" stroke="#6b4f12" strokeWidth="0.5"/>
    <text x="12" y="15.5" fontSize="4" fill="#5a4510" textAnchor="middle" fontFamily="serif" fontWeight="bold">U</text>
  </svg>
)

// Lock Icon - Security
const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
    <defs>
      <linearGradient id="lockGold3" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e6c84a"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    <rect x="5" y="11" width="14" height="10" rx="2" fill="url(#lockGold3)" stroke="#5a4510" strokeWidth="1"/>
    <path d="M8 11 L8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7 L16 11" fill="none" stroke="url(#lockGold3)" strokeWidth="2"/>
    <circle cx="12" cy="16" r="1.5" fill="#2a1a10"/>
  </svg>
)

// Shopping Cart Icon - Medieval Treasure Chest
const ShoppingCartIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" className="account__info-icon">
    <defs>
      <linearGradient id="chestWood" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#8b5a2b"/>
        <stop offset="50%" stopColor="#6b4423"/>
        <stop offset="100%" stopColor="#4a2f17"/>
      </linearGradient>
      <linearGradient id="chestMetal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <filter id="chestShadow">
        <feDropShadow dx="0" dy="1" stdDeviation="0.8" floodColor="#2a1a0a" floodOpacity="0.4"/>
      </filter>
    </defs>
    {/* Chest base */}
    <rect x="3" y="12" width="18" height="9" rx="1" fill="url(#chestWood)" filter="url(#chestShadow)"/>
    {/* Chest lid */}
    <path d="M3 12 Q3 8 12 8 Q21 8 21 12 L21 12 L3 12 Z" fill="url(#chestWood)" stroke="#4a2f17" strokeWidth="0.5"/>
    {/* Metal bands */}
    <rect x="3" y="11" width="18" height="2" fill="url(#chestMetal)" rx="0.5"/>
    <rect x="3" y="17" width="18" height="1.5" fill="url(#chestMetal)" rx="0.3"/>
    {/* Lock/clasp */}
    <rect x="10" y="13" width="4" height="3" rx="0.5" fill="url(#chestMetal)" stroke="#6b4f12" strokeWidth="0.3"/>
    <circle cx="12" cy="14.5" r="0.8" fill="#4a2f17"/>
    {/* Decorative corners */}
    <circle cx="5" cy="14" r="1" fill="url(#chestMetal)"/>
    <circle cx="19" cy="14" r="1" fill="url(#chestMetal)"/>
    {/* Shine on lid */}
    <path d="M6 10 Q12 7 18 10" fill="none" stroke="#d4a857" strokeWidth="0.5" opacity="0.6"/>
  </svg>
)

// Medieval Coin Icon - Premium detailed version
const CoinIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="account__coin-icon">
    <defs>
      <linearGradient id="coinGoldMain" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="25%" stopColor="#e6c84a"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="75%" stopColor="#b8960f"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
      <radialGradient id="coinShineMain" cx="30%" cy="30%">
        <stop offset="0%" stopColor="#fff8dc" stopOpacity="0.8"/>
        <stop offset="50%" stopColor="#ffd700" stopOpacity="0.3"/>
        <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
      </radialGradient>
      <filter id="coinShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#5a4510" floodOpacity="0.4"/>
      </filter>
    </defs>
    {/* Outer ring */}
    <circle cx="12" cy="12" r="10.5" fill="url(#coinGoldMain)" stroke="#6b4f12" strokeWidth="0.5" filter="url(#coinShadow)"/>
    {/* Inner decorative ring */}
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="#a07818" strokeWidth="1" strokeDasharray="2 1"/>
    {/* Center shine */}
    <circle cx="12" cy="12" r="7" fill="url(#coinShineMain)"/>
    {/* Crown symbol */}
    <path d="M8 13 L9 10 L10.5 12 L12 9 L13.5 12 L15 10 L16 13 L15 14 L9 14 Z" fill="#6b4f12" opacity="0.7"/>
    {/* Decorative dots */}
    <circle cx="12" cy="6" r="0.8" fill="#6b4f12" opacity="0.5"/>
    <circle cx="12" cy="18" r="0.8" fill="#6b4f12" opacity="0.5"/>
    <circle cx="6" cy="12" r="0.8" fill="#6b4f12" opacity="0.5"/>
    <circle cx="18" cy="12" r="0.8" fill="#6b4f12" opacity="0.5"/>
  </svg>
)

// Medieval Scroll Icon - Detailed parchment
const ScrollIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="account__scroll-icon">
    <defs>
      <linearGradient id="scrollPaperMain" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#faf6f0"/>
        <stop offset="20%" stopColor="#f5edd8"/>
        <stop offset="80%" stopColor="#e8dcc4"/>
        <stop offset="100%" stopColor="#d4c4a8"/>
      </linearGradient>
      <linearGradient id="scrollWood" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#8b7355"/>
        <stop offset="50%" stopColor="#6b5340"/>
        <stop offset="100%" stopColor="#8b7355"/>
      </linearGradient>
      <filter id="scrollShadow">
        <feDropShadow dx="0" dy="1" stdDeviation="0.5" floodColor="#3d2b1f" floodOpacity="0.3"/>
      </filter>
    </defs>
    {/* Main scroll body */}
    <rect x="6" y="5" width="12" height="14" fill="url(#scrollPaperMain)" rx="1" filter="url(#scrollShadow)"/>
    {/* Top roll */}
    <ellipse cx="12" cy="5" rx="7" ry="2" fill="url(#scrollWood)"/>
    <ellipse cx="12" cy="5" rx="6" ry="1.5" fill="url(#scrollPaperMain)"/>
    {/* Bottom roll */}
    <ellipse cx="12" cy="19" rx="7" ry="2" fill="url(#scrollWood)"/>
    <ellipse cx="12" cy="19" rx="6" ry="1.5" fill="url(#scrollPaperMain)"/>
    {/* Wood end caps */}
    <circle cx="5" cy="5" r="1.5" fill="#6b5340" stroke="#4a3828" strokeWidth="0.3"/>
    <circle cx="19" cy="5" r="1.5" fill="#6b5340" stroke="#4a3828" strokeWidth="0.3"/>
    <circle cx="5" cy="19" r="1.5" fill="#6b5340" stroke="#4a3828" strokeWidth="0.3"/>
    <circle cx="19" cy="19" r="1.5" fill="#6b5340" stroke="#4a3828" strokeWidth="0.3"/>
    {/* Text lines */}
    <line x1="8" y1="9" x2="16" y2="9" stroke="#8b7355" strokeWidth="0.7" strokeLinecap="round"/>
    <line x1="8" y1="11.5" x2="16" y2="11.5" stroke="#8b7355" strokeWidth="0.7" strokeLinecap="round"/>
    <line x1="8" y1="14" x2="14" y2="14" stroke="#8b7355" strokeWidth="0.7" strokeLinecap="round"/>
  </svg>
)

// PIX Icon - Brazilian Payment
const PixIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="account__pix-icon">
    <defs>
      <linearGradient id="pixGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00d4d4"/>
        <stop offset="100%" stopColor="#00a8a8"/>
      </linearGradient>
    </defs>
    <path d="M12 2 L2 12 L12 22 L22 12 Z" fill="url(#pixGradient)" stroke="#008080" strokeWidth="1.5"/>
    <path d="M8 12 L12 8 L16 12 L12 16 Z" fill="white"/>
    <path d="M12 6 L18 12 L12 18 L6 12 Z" fill="none" stroke="white" strokeWidth="1.5"/>
  </svg>
)

// Copy Icon
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="account__copy-icon">
    <rect x="9" y="9" width="13" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
    <path d="M5 15 L5 5 C5 3.9 5.9 3 7 3 L15 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

// Medieval Coin Bag Icon - For savings/discount
const CoinBagIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="account__coinbag-icon">
    <defs>
      <linearGradient id="bagBrown" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8b7355"/>
        <stop offset="50%" stopColor="#6b5340"/>
        <stop offset="100%" stopColor="#5a4a3a"/>
      </linearGradient>
      <radialGradient id="bagShine" cx="40%" cy="30%">
        <stop offset="0%" stopColor="#a08060" stopOpacity="0.6"/>
        <stop offset="100%" stopColor="#6b5340" stopOpacity="0"/>
      </radialGradient>
    </defs>
    {/* Bag body */}
    <path d="M8 10 C8 10 7 11 7 14 C7 18 9 21 12 21 C15 21 17 18 17 14 C17 11 16 10 16 10" 
          fill="url(#bagBrown)" stroke="#4a3828" strokeWidth="0.8"/>
    {/* Bag opening */}
    <ellipse cx="12" cy="10" rx="4.5" ry="2" fill="#4a3828"/>
    <ellipse cx="12" cy="9.5" rx="4" ry="1.5" fill="#6b5340"/>
    {/* Rope/tie */}
    <path d="M10 8 Q10 6 12 6 Q14 6 14 8" fill="none" stroke="#8b7355" strokeWidth="1.2" strokeLinecap="round"/>
    {/* Coins symbol */}
    <circle cx="12" cy="14" r="2.5" fill="#c9a227" opacity="0.7" stroke="#8b6914" strokeWidth="0.5"/>
    <text x="12" y="15.5" fontSize="3" fontWeight="bold" textAnchor="middle" fill="#6b4f12">$</text>
    {/* Shine effect */}
    <ellipse cx="10" cy="12" rx="2" ry="3" fill="url(#bagShine)"/>
  </svg>
)

// Check Icon
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="account__check-icon">
    <path d="M20 6 L9 17 L4 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__whatsapp-icon">
    <defs>
      <linearGradient id="waGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#25d366"/>
        <stop offset="100%" stopColor="#128c7e"/>
      </linearGradient>
    </defs>
    <path d="M12 2 C6.5 2 2 6.5 2 12 C2 13.8 2.5 15.5 3.3 17 L2 22 L7.2 20.7 C8.7 21.5 10.3 22 12 22 C17.5 22 22 17.5 22 12 C22 6.5 17.5 2 12 2 Z" fill="url(#waGradient)"/>
    <path d="M16.8 14.5 C16.6 14.4 15.6 13.9 15.4 13.8 C15.2 13.7 15.1 13.7 15 13.9 C14.9 14.1 14.5 14.6 14.4 14.7 C14.3 14.8 14.2 14.8 14 14.7 C13.8 14.6 13.1 14.4 12.3 13.7 C11.7 13.1 11.2 12.4 11.1 12.2 C11 12 11.1 11.9 11.2 11.8 C11.3 11.7 11.4 11.5 11.5 11.4 C11.6 11.3 11.6 11.2 11.7 11.1 C11.8 11 11.8 10.9 11.7 10.8 C11.7 10.7 11.2 9.7 11 9.3 C10.8 8.9 10.6 9 10.5 9 L10.1 9 C10 9 9.8 9 9.6 9.2 C9.4 9.4 8.9 9.9 8.9 10.9 C8.9 11.9 9.6 12.9 9.7 13 C9.8 13.1 11.2 15.2 13.4 16.1 C15.6 17 15.6 16.6 16.2 16.5 C16.8 16.4 17.6 16 17.8 15.5 C18 15 18 14.6 17.9 14.5 C17.8 14.5 17 14.6 16.8 14.5 Z" fill="white"/>
  </svg>
)

// Email Support Icon
const EmailSupportIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="account__email-support-icon">
    <defs>
      <linearGradient id="emailSupportGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b82f6"/>
        <stop offset="100%" stopColor="#2563eb"/>
      </linearGradient>
    </defs>
    <rect x="2" y="4" width="20" height="16" rx="2" fill="url(#emailSupportGrad)"/>
    <path d="M2 6 L12 13 L22 6" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="17" cy="17" r="4" fill="#16a34a" stroke="white" strokeWidth="1.5"/>
    <path d="M15.5 17 L16.5 18 L18.5 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// Character Icon - Warrior
const CharacterIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="account__char-icon">
    <defs>
      <linearGradient id="charGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e6c84a"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    <circle cx="12" cy="7" r="4" fill="url(#charGold)"/>
    <path d="M5 21 C5 15 8 12 12 12 C16 12 19 15 19 21" fill="url(#charGold)"/>
    <path d="M12 4 L12 2" stroke="#c9a227" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 5 L7 3" stroke="#c9a227" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M15 5 L17 3" stroke="#c9a227" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

// World Icon - Globe
const WorldIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="account__world-icon">
    <circle cx="12" cy="12" r="9" fill="none" stroke="#8b7355" strokeWidth="1.5"/>
    <ellipse cx="12" cy="12" rx="4" ry="9" fill="none" stroke="#8b7355" strokeWidth="1"/>
    <line x1="3" y1="12" x2="21" y2="12" stroke="#8b7355" strokeWidth="1"/>
    <path d="M4 8 Q12 6 20 8" fill="none" stroke="#8b7355" strokeWidth="0.75"/>
    <path d="M4 16 Q12 18 20 16" fill="none" stroke="#8b7355" strokeWidth="0.75"/>
  </svg>
)

// Online Status Icon
const OnlineIcon = ({ isOnline }) => (
  <svg viewBox="0 0 12 12" width="10" height="10" className="account__status-icon">
    <circle cx="6" cy="6" r="5" fill={isOnline ? "#22c55e" : "#6b7280"} stroke={isOnline ? "#16a34a" : "#4b5563"} strokeWidth="1"/>
  </svg>
)

// Level Icon - Star
const LevelIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" className="account__level-icon">
    <polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" fill="#c9a227" stroke="#8b6914" strokeWidth="1"/>
  </svg>
)

// Refresh Icon
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="account__refresh-icon">
    <path d="M21 12 A9 9 0 1 1 12 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M21 3 L21 9 L15 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// Loading Spinner
const LoadingSpinner = ({ text = 'Loading...' }) => (
  <div className="account__loading">
    <div className="account__spinner"></div>
    <span>{text}</span>
  </div>
)

// Authenticated Image Component with Skeleton Loading
const AuthImage = ({ clientId, filename, alt, onLoad, onError, className }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    let objectUrl = null

    const loadImage = async () => {
      setIsLoading(true)
      try {
        // Add cache busting to force fresh image load
        const result = await API.Images.get(clientId, filename)
        
        if (result.success && result.blob && mounted) {
          // Revoke old URL before creating new one
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl)
          }
          
          objectUrl = URL.createObjectURL(result.blob)
          setImageSrc(objectUrl)
          setImageError(false)
          setIsLoading(false)
          if (onLoad) onLoad()
        } else {
          setImageError(true)
          setIsLoading(false)
          if (onError) onError()
        }
      } catch (err) {
        if (mounted) {
          setImageError(true)
          setIsLoading(false)
          if (onError) onError()
        }
      }
    }

    if (clientId && filename) {
      // Reset states before loading
      setImageSrc(null)
      setImageError(false)
      setIsLoading(true)
      loadImage()
    }

    return () => {
      mounted = false
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [clientId, filename])

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className="account__char-avatar-skeleton-wrapper">
        <SkeletonAvatar size="48px" />
      </div>
    )
  }

  if (imageError || !imageSrc) {
    return null
  }

  return <img src={imageSrc} alt={alt} className={`${className} account__char-img-loaded`} />
}

// Vocation Icons based on name
const getVocationIcon = (vocation) => {
  if (!vocation) return '⚔️'
  const v = vocation.toLowerCase()
  if (v.includes('knight') || v.includes('elite')) return '🛡️'
  if (v.includes('sorcerer') || v.includes('mage')) return '🔮'
  if (v.includes('druid')) return '🌿'
  if (v.includes('paladin')) return '🏹'
  if (v.includes('monk')) return '☯️'
  return '⚔️'
}

const AccountModal = ({ isOpen, onClose, user, onLogout, onUserUpdate, initialTab = 'info' }) => {
  const [accountInfo, setAccountInfo] = useState(null)
  const [license, setLicense] = useState(null)
  const [characters, setCharacters] = useState([])
  const [onlineStatus, setOnlineStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [refreshSuccess, setRefreshSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [loadedImages, setLoadedImages] = useState({})
  const [lastRefreshTime, setLastRefreshTime] = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)
  
  // Character detail modal state
  const [selectedCharacter, setSelectedCharacter] = useState(null)
  const [characterModalOpen, setCharacterModalOpen] = useState(false)
  const [loadingCharacter, setLoadingCharacter] = useState(false)

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  // Nickname edit state
  const [nicknameForm, setNicknameForm] = useState({ nickname: '' })
  const [nicknameError, setNicknameError] = useState('')
  const [nicknameSuccess, setNicknameSuccess] = useState('')
  const [nicknameLoading, setNicknameLoading] = useState(false)
  const [editingNickname, setEditingNickname] = useState(false)

  // Buy Tabs state
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [purchaseLoading, setPurchaseLoading] = useState(false)
  const [purchaseError, setPurchaseError] = useState('')
  const [purchaseSuccess, setPurchaseSuccess] = useState('')
  const [showPixPayment, setShowPixPayment] = useState(false)
  const [pixKeyCopied, setPixKeyCopied] = useState(false)
  const [selectedTabs, setSelectedTabs] = useState(3)
  const [selectedMonths, setSelectedMonths] = useState(1)

  // Bot info state (public)
  const [botStatus, setBotStatus] = useState(null)
  const [botChangelog, setBotChangelog] = useState([])
  const [botLoading, setBotLoading] = useState(false)
  const [botDownloading, setBotDownloading] = useState(false)
  const [showAllChangelogs, setShowAllChangelogs] = useState(false)
  const [downloadingPatch, setDownloadingPatch] = useState(false)

  // Email verification state
  const [verifLoading, setVerifLoading] = useState(false)
  const [verifMessage, setVerifMessage] = useState('')
  const [verifMessageType, setVerifMessageType] = useState('')

  // Bot Config tab state
  const [configSubTab, setConfigSubTab] = useState('telegram')
  const [configLoading, setConfigLoading] = useState(false)
  const [configError, setConfigError] = useState('')
  // Telegram
  const [telegramConfigs, setTelegramConfigs] = useState([])
  // Binds
  const [bindConfigs, setBindConfigs] = useState([])
  // Unreal
  const [unrealCredentials, setUnrealCredentials] = useState([])
  // Life Insurance
  const [lifeInsurancePrefs, setLifeInsurancePrefs] = useState([])

  // Support tab state
  const [serverStatus, setServerStatus] = useState(null)
  const [supportLoading, setSupportLoading] = useState(false)

  // PIX Configuration
  const PIX_KEY = '7de2654b-333d-4f9e-8719-faa2b391a432'
  const SUPPORT_EMAIL = 'support@unity-bot.com'
  const SUPPORT_WHATSAPP = '5535997150413'
  const SUPPORT_WHATSAPP_DISPLAY = '35 99715-0413'
  const [pixPayload, setPixPayload] = useState('')
  
  // Função para gerar PIX Copia e Cola (EMV QR Code)
  const generatePixPayload = (amount) => {
    const pixKey = PIX_KEY
    const merchantName = 'Rodiney Garcia Alves'
    const merchantCity = 'Sao Paulo'
    const txid = `UNITY${Date.now().toString().slice(-10)}`
    
    // Formato EMV correto
    const keyLength = pixKey.length.toString().padStart(2, '0')
    const keyField = `0014br.gov.bcb.pix01${keyLength}${pixKey}`
    const keyFieldLength = keyField.length.toString().padStart(2, '0')
    
    const amountStr = amount.toFixed(2)
    const amountLength = amountStr.length.toString().padStart(2, '0')
    
    const nameLength = merchantName.length.toString().padStart(2, '0')
    const cityLength = merchantCity.length.toString().padStart(2, '0')
    
    const txidField = `05${txid.length.toString().padStart(2, '0')}${txid}`
    const txidFieldLength = txidField.length.toString().padStart(2, '0')
    
    // Montar payload sem CRC
    let payload = '00020126' // Payload Format Indicator
    payload += keyFieldLength + keyField // Merchant Account Information
    payload += '52040000' // Merchant Category Code
    payload += '5303986' // Currency Code (BRL)
    payload += '54' + amountLength + amountStr // Transaction Amount
    payload += '5802BR' // Country Code
    payload += '59' + nameLength + merchantName // Merchant Name
    payload += '60' + cityLength + merchantCity // Merchant City
    payload += '62' + txidFieldLength + txidField // Additional Data Field
    payload += '6304' // CRC16 placeholder
    
    // Calcular CRC16
    const crc = calculateCRC16(payload)
    payload += crc
    
    return payload
  }
  
  // Calcular CRC16 CCITT para PIX
  const calculateCRC16 = (payload) => {
    let crc = 0xFFFF
    for (let i = 0; i < payload.length; i++) {
      crc ^= payload.charCodeAt(i) << 8
      for (let j = 0; j < 8; j++) {
        if ((crc & 0x8000) !== 0) {
          crc = (crc << 1) ^ 0x1021
        } else {
          crc = crc << 1
        }
      }
    }
    return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0')
  }

  // Update active tab when initialTab prop changes
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTab])

  // Fetch all account data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchAllData()
      // Clear password messages when modal opens
      setPasswordError('')
      setPasswordSuccess('')
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      // Initialize nickname from user data
      setNicknameForm({ nickname: user.nickname || '' })
      setNicknameError('')
      setNicknameSuccess('')
      setEditingNickname(false)
    }
  }, [isOpen, user])

  // Auto-refresh system - intelligent polling
  useEffect(() => {
    if (!isOpen || !user || !autoRefreshEnabled) return

    // Refresh interval: 30 seconds when tab visible, 5 minutes when hidden
    const getRefreshInterval = () => {
      return document.hidden ? 300000 : 30000 // 5min : 30s
    }

    // Initial refresh interval
    let refreshInterval = setInterval(() => {
      if (document.hidden) return // Skip if tab is hidden
      fetchAllData(true) // true = silent refresh (no loading spinner)
    }, getRefreshInterval())

    // Listen for visibility changes to adjust refresh rate
    const handleVisibilityChange = () => {
      clearInterval(refreshInterval)
      
      if (!document.hidden && isOpen) {
        // Tab became visible and modal is open - refresh immediately
        fetchAllData(true)
      }
      
      // Restart interval with new timing
      refreshInterval = setInterval(() => {
        if (document.hidden) return
        fetchAllData(true)
      }, getRefreshInterval())
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(refreshInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isOpen, user, autoRefreshEnabled])

  const fetchAllData = async (silent = false) => {
    const hadData = Boolean(accountInfo)

    if (!silent) {
      // If we already have data, perform a silent/partial refresh to avoid layout swap
      if (hadData) {
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
    }
    setError(null)

    try {
      // Fetch profile
      const profileRes = await API.Auth.getProfile()
      
      if (profileRes.success) {
        const clientData = profileRes.data?.client || profileRes.data
        setAccountInfo(clientData)
        
        // If we have clientId, fetch license, characters, and online status
        const clientId = clientData?.id || user?.id
        
        if (clientId) {
          // Fetch license, characters, and online status in parallel
          const [licenseRes, charsRes, onlineRes] = await Promise.all([
            API.Licenses.getClientLicense(clientId),
            API.Characters.list(clientId),
            API.Auth.getOnlineStatus()
          ])

          if (licenseRes.success) {
            setLicense(licenseRes.data)
          }

          if (charsRes.success) {
            setCharacters(charsRes.data || [])
            
            // Force image reload by clearing loaded images state
            setLoadedImages({})
          } else {
            console.error('Failed to load characters:', charsRes.error)
          }

          if (onlineRes.success && onlineRes.data) {
            setOnlineStatus(onlineRes.data)
          }

          // Fetch server status silently (non-blocking)
          API.Server.getStatus().then(statusRes => {
            if (statusRes.success) {
              setServerStatus(statusRes.data)
            }
          })
        }
        
        // Update last refresh time
        setLastRefreshTime(new Date())
        // Show success feedback for manual refreshes
        if (!silent) {
          setRefreshSuccess(true)
          setTimeout(() => setRefreshSuccess(false), 1600)
        }
      } else {
        setError(profileRes.error || 'Failed to load profile')
      }
    } catch (err) {
      console.error('Failed to fetch account data:', err)
      setError('Could not load account information')
    } finally {
      if (!silent) {
        if (hadData) {
          setIsRefreshing(false)
        } else {
          setLoading(false)
        }
      }
    }
  }

  const handleCharacterClick = async (char) => {
    setLoadingCharacter(true)
    try {
      // Buscar dados completos do personagem
      const response = await API.Characters.getByNameWorld(char.name, char.world)
      
      if (response.success && response.data) {
        setSelectedCharacter(response.data)
        setCharacterModalOpen(true)
      } else {
        // Se falhar, abre com dados básicos
        setSelectedCharacter({ name: char.name, world: char.world, ...char })
        setCharacterModalOpen(true)
      }
    } catch (err) {
      console.error('Failed to fetch character details:', err)
      // Abre modal com dados básicos em caso de erro
      setSelectedCharacter({ name: char.name, world: char.world, ...char })
      setCharacterModalOpen(true)
    } finally {
      setLoadingCharacter(false)
    }
  }

  const closeCharacterModal = () => {
    setCharacterModalOpen(false)
    setSelectedCharacter(null)
  }

  const handleDeleteCharacter = async (name, world) => {
    try {
      // Admin pode passar clientId para deletar de outro usuário
      const targetClientId = user?.isAdmin ? selectedCharacter?.clientId : null
      
      const result = await API.Characters.deleteByNameWorld(name, world, targetClientId)
      
      if (result.success) {
        // Update the characters list by removing the deleted character
        setCharacters(prevChars => 
          prevChars.filter(char => !(char.name === name && char.world === world))
        )
        // Close the character modal
        closeCharacterModal()
      } else {
        throw new Error(result.error || 'Failed to delete character')
      }
    } catch (error) {
      console.error('Error deleting character:', error)
      throw error
    }
  }

  if (!user) return null

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTimeSince = (date) => {
    if (!date) return 'never'
    const seconds = Math.floor((new Date() - date) / 1000)
    
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  const formatMemberDuration = (dateString) => {
    if (!dateString) return ''
    const created = new Date(dateString)
    const now = new Date()
    const diffMs = now - created
    const diffYears = Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000))
    const diffMonths = Math.floor((diffMs % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000))
    if (diffYears > 0) return `Member for ${diffYears}y ${diffMonths}m`
    if (diffMonths > 0) return `Member for ${diffMonths}m`
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000))
    if (diffDays > 0) return `Member for ${diffDays}d`
    return 'Member for < 1d'
  }

  const formatTimeAgo = (dateString) => {
    if (!dateString) return ''
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now - date
    const diffSec = Math.floor(diffMs / 1000)
    if (diffSec < 60) return 'just now'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDays = Math.floor(diffHr / 24)
    if (diffDays < 30) return `${diffDays}d ago`
    return `${Math.floor(diffDays / 30)}mo ago`
  }

  const getTotalTabs = () => {
    if (!license) return 0
    // API returns summary object with totalAvailableTabs
    return license.summary?.totalAvailableTabs || license.totalAvailableTabs || (license.tabs || 0)
  }

  const getLicenseStatus = () => {
    if (!license) return { text: 'Free', class: 'free' }
    const tabs = getTotalTabs()
    if (tabs > 0) return { text: 'Licensed', class: 'premium' }
    return { text: 'Free', class: 'free' }
  }

  const getAccountStatus = () => {
    if (!accountInfo) return { text: 'Unknown', class: 'unknown' }
    if (accountInfo.isActive) return { text: 'Active', class: 'active' }
    if (accountInfo.inactiveUntil) return { text: 'Suspended', class: 'suspended' }
    return { text: 'Banned', class: 'banned' }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    // Clear password messages when leaving security tab
    if (tab !== 'security') {
      setPasswordError('')
      setPasswordSuccess('')
    }
    // Clear nickname messages when leaving info tab
    if (tab !== 'info') {
      setNicknameError('')
      setNicknameSuccess('')
      setEditingNickname(false)
    }
    // Clear purchase messages when leaving buy tab
    if (tab !== 'buy') {
      setPurchaseError('')
      setPurchaseSuccess('')
      setSelectedPackage(null)
      setShowPixPayment(false)
      setPixKeyCopied(false)
      setPixPayload('')
    }
    // Load bot data when switching to bot tab
    if (tab === 'bot' && !botStatus) {
      fetchBotData()
    }
    // Load support data when switching to support tab
    if (tab === 'support' && !serverStatus && !supportLoading) {
      fetchSupportData()
    }
    // Clear config messages when leaving config tab
    // Load bot config data when switching to config tab
    if (tab === 'config') {
      fetchConfigData()
    }
  }

  const fetchBotData = async () => {
    setBotLoading(true)
    try {
      const [statusRes, changelogRes] = await Promise.all([
        API.Bot.getLatest(),
        API.Bot.getChangelog()
      ])
      if (statusRes.success) setBotStatus(statusRes.data)
      if (changelogRes.success) setBotChangelog(changelogRes.data?.changelog || [])
    } catch (err) {
      console.error('Failed to fetch bot data:', err)
    } finally {
      setBotLoading(false)
    }
  }

  const fetchConfigData = async () => {
    setConfigLoading(true)
    setConfigError('')
    try {
      const [tgRes, bindRes, ucRes, lifeRes] = await Promise.all([
        API.BotConfigs.getAllTelegram(),
        API.BotConfigs.getAllBinds(),
        API.BotConfigs.getAllUnrealCredentials(),
        API.BotConfigs.getAllLifeInsurance()
      ])
      if (tgRes.success) setTelegramConfigs(tgRes.data || [])
      if (bindRes.success) setBindConfigs(bindRes.data || [])
      if (ucRes.success) setUnrealCredentials(ucRes.data || [])
      if (lifeRes.success) setLifeInsurancePrefs(lifeRes.data || [])
    } catch (err) {
      console.error('Failed to fetch config data:', err)
    } finally {
      setConfigLoading(false)
    }
  }

  const fetchSupportData = async () => {
    setSupportLoading(true)
    try {
      const statusRes = await API.Server.getStatus()
      if (statusRes.success) {
        setServerStatus(statusRes.data)
      }
    } catch (err) {
      console.error('Failed to fetch support data:', err)
    } finally {
      setSupportLoading(false)
    }
  }

  const handleDownloadBot = async (e) => {
    e.preventDefault()
    
    if (botDownloading) return
    
    setBotDownloading(true)
    
    const result = await downloadInstaller()
    
    if (result.success) {
      showDownloadSuccess(result.message)
    } else {
      showDownloadError(result.error)
    }
    
    setBotDownloading(false)
  }

    const handleDownloadPatch = async (e) => {
    e.preventDefault()
    
    if (downloadingPatch) return
    
    setDownloadingPatch(true)
    
    const result = await downloadPatch()
    
    if (result.error) {
      showDownloadError(result.error)
    }
    
    setDownloadingPatch(false)
  }

  const handleResendVerification = async () => {
    if (verifLoading) return
    setVerifLoading(true)
    setVerifMessage('')
    try {
      const username = accountInfo?.username || user?.username
      if (!username) throw new Error('Username not found')
      const result = await API.Auth.resendVerification(username)
      if (result.success) {
        setVerifMessage('Verification email sent!')
        setVerifMessageType('success')
      } else {
        setVerifMessage(result.error || 'Failed to send verification email')
        setVerifMessageType('error')
      }
    } catch (err) {
      setVerifMessage('Could not send verification email. Try again later.')
      setVerifMessageType('error')
    } finally {
      setVerifLoading(false)
      setTimeout(() => setVerifMessage(''), 5000)
    }
  }

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    })
    setPasswordError('')
    setPasswordSuccess('')
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required')
      return
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters')
      return
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    setPasswordLoading(true)

    try {
      const result = await API.Auth.changePassword(passwordForm.currentPassword, passwordForm.newPassword)
      
      if (result.success) {
        setPasswordSuccess('✅ Password changed successfully!')
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        
        // Re-login with new password to maintain session
        setTimeout(async () => {
          try {
            const username = accountInfo?.username || user?.username
            if (username) {
              await API.Auth.login(username, passwordForm.newPassword)
            }
          } catch (err) {
            console.error('Auto-login after password change failed:', err)
          }
        }, 1000)
      } else {
        setPasswordError(typeof result.error === 'object' ? result.error?.message || 'Failed to change password' : result.error)
      }
    } catch (err) {
      setPasswordError('An error occurred. Please try again.')
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleLogout = async () => {
    await API.Auth.logout()
    onLogout()
    onClose()
  }

  const handleNicknameChange = (e) => {
    const value = e.target.value
    setNicknameForm({ nickname: value })
    setNicknameSuccess('')

    const error = validateNicknameLocal(value)
    setNicknameError(error || '')
  }

  const handleNicknameSubmit = async (e) => {
    e.preventDefault()
    setNicknameError('')
    setNicknameSuccess('')

    const newNickname = nicknameForm.nickname.trim()

    const validationError = validateNicknameLocal(newNickname)
    if (validationError) {
      setNicknameError(validationError)
      return
    }

    setNicknameLoading(true)

    try {
      const result = await API.Clients.updateNickname(newNickname)
      
      if (result.success) {
        setNicknameSuccess('✅ Nickname updated successfully!')
        setEditingNickname(false)
        
        // Get updated nickname from API response
        const updatedNickname = result.data?.nickname ?? newNickname
        
        // Update user in localStorage
        const updatedUser = { ...user, nickname: updatedNickname }
        localStorage.setItem('unity-auth-user', JSON.stringify(updatedUser))
        
        // Update global user state through callback
        if (onUserUpdate) {
          onUserUpdate({ nickname: updatedNickname })
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setNicknameSuccess('')
        }, 3000)
      } else {
        setNicknameError(typeof result.error === 'object' ? result.error?.message || 'Failed to update nickname' : result.error)
      }
    } catch (err) {
      setNicknameError('An error occurred. Please try again.')
    } finally {
      setNicknameLoading(false)
    }
  }

  // Dynamic package configuration
  const TAB_PRICE = 6 // R$6 per tab per month
  
  // Discount tiers based on months: longer durations get better discounts
  const getDiscountRate = (tabs, months) => {
    // Defensive defaults
    const t = Math.max(0, parseInt(tabs || 0))
    const m = Math.max(0, parseInt(months || 0))

    // Month-based discounts (stronger incentives for longer commitments)
    let monthDiscount = 0
    if (m <= 1) monthDiscount = 0
    else if (m === 2) monthDiscount = 0.04
    else if (m === 3) monthDiscount = 0.10
    else if (m === 4) monthDiscount = 0.14
    else if (m === 5) monthDiscount = 0.18
    else monthDiscount = 0.25 // 6+ months (strong incentive)

    // Tab-based discounts (volume discount)
    let tabDiscount = 0
    if (t <= 2) tabDiscount = 0
    else if (t <= 4) tabDiscount = 0.05
    else if (t <= 7) tabDiscount = 0.08
    else tabDiscount = 0.12 // 8-10

    // Combine discounts but cap to prevent excessive values
    const combined = monthDiscount + tabDiscount
    const capped = Math.min(combined, 0.50) // max 50% off
    // Return detailed breakdown (percent values as decimal)
    return {
      monthDiscount: Math.round(monthDiscount * 100) / 100,
      tabDiscount: Math.round(tabDiscount * 100) / 100,
      combined: Math.round(capped * 100) / 100
    }
  }

  // Calculate price dynamically with discount applied
  const calculatePrice = (tabs, months) => {
    const base = (tabs || 0) * (months || 0) * TAB_PRICE
    const discounts = getDiscountRate(tabs, months)
    const discount = discounts?.combined || 0
    const total = base * (1 - discount)
    return Math.round(total * 100) / 100 // keep two-decimal precision
  }
  
  // Calculate slider progress for visual effect
  const calculateSliderProgress = (value, min, max) => {
    return ((value - min) / (max - min)) * 100
  }
  
  const handleTabsChange = (value) => {
    setSelectedTabs(parseInt(value))
    setPurchaseError('')
    setPurchaseSuccess('')
  }
  
  const handleMonthsChange = (value) => {
    setSelectedMonths(parseInt(value))
    setPurchaseError('')
    setPurchaseSuccess('')
  }
  
  // Quick select presets with icons and badges
  const QUICK_PRESETS = [
    { tabs: 1, months: 1, label: 'Básico', icon: 'shield', badge: null },
    { tabs: 3, months: 1, label: 'Popular', icon: 'sword', badge: '🔥' },
    { tabs: 5, months: 3, label: 'Premium', icon: 'crown', badge: '⭐' },
    { tabs: 5, months: 6, label: 'Ultimate', icon: 'crown', badge: '👑', featured: true },
  ]
  
  // Get preset icon component
  const getPresetIcon = (iconName) => {
    switch (iconName) {
      case 'shield': return <ShieldIcon size={20} />
      case 'sword': return <SwordIcon size={20} />
      case 'crown': return <CrownIcon size={20} />
      default: return <TabIcon />
    }
  }
  
  const handlePresetSelect = (preset) => {
    setSelectedTabs(preset.tabs)
    setSelectedMonths(preset.months)
    setPurchaseError('')
    setPurchaseSuccess('')
  }

  const handlePurchase = async () => {
    if (selectedTabs < 1 || selectedMonths < 1) {
      setPurchaseError('Por favor, selecione a quantidade de tabs e duração')
      return
    }

    setPurchaseLoading(true)
    setPurchaseError('')
    setPurchaseSuccess('')

    try {
      // Simular validação
      await new Promise(resolve => setTimeout(resolve, 800))
      
      // Criar pacote dinâmico
      const dynamicPackage = {
        tabs: selectedTabs,
        months: selectedMonths,
        price: calculatePrice(selectedTabs, selectedMonths)
      }
      setSelectedPackage(dynamicPackage)
      
      // Mostrar tela de pagamento PIX
      setShowPixPayment(true)
      // Gerar e armazenar o payload PIX agora para que QR e botão copiar usem
      try {
        const payload = generatePixPayload(dynamicPackage.price)
        setPixPayload(payload)
      } catch (err) {
        console.error('Failed to generate PIX payload:', err)
        setPixPayload('')
      }
      setPurchaseSuccess('Pedido confirmado! Realize o pagamento via PIX para ativar suas tabs.')
      
      // Scroll to QR Code section after state update
      setTimeout(() => {
        const qrSection = document.querySelector('.account__qr-section')
        if (qrSection) {
          qrSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
      
    } catch (err) {
      setPurchaseError('Falha ao processar pedido. Tente novamente ou contate o suporte.')
    } finally {
      setPurchaseLoading(false)
    }
  }
  
  const copyPixKey = async () => {
    try {
      const pixCode = pixPayload || generatePixPayload(selectedPackage?.price || calculatePrice(selectedTabs, selectedMonths))
      await navigator.clipboard.writeText(pixCode)
      setPixKeyCopied(true)
      setTimeout(() => setPixKeyCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy PIX code:', err)
    }
  }
  
  const openWhatsApp = () => {
    const price = selectedPackage ? selectedPackage.price : calculatePrice(selectedTabs, selectedMonths)
    const tabs = selectedPackage ? selectedPackage.tabs : selectedTabs
    const months = selectedPackage ? selectedPackage.months : selectedMonths
    const message = `Olá! Gostaria de confirmar meu pagamento de R$${price.toFixed(2)} referente a ${tabs} tab(s) por ${months} mês(es). Minha conta: "${accountInfo?.username || user?.username}", aguardo retorno. Obrigado!`
    const url = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }
  
  const openEmail = () => {
    const subject = 'Confirmação de Pagamento - Unity Tabs'
    const price = selectedPackage ? selectedPackage.price : calculatePrice(selectedTabs, selectedMonths)
    const tabs = selectedPackage ? selectedPackage.tabs : selectedTabs
    const months = selectedPackage ? selectedPackage.months : selectedMonths
    const body = `Olá,\n\nRealizei o pagamento de R$${price.toFixed(2)} via PIX para aquisição de ${tabs} tab(s) por ${months} mês(es).\n\nChave PIX: ${PIX_KEY}\n\nAguardo confirmação.\n\nObrigado! Minha conta: "${accountInfo?.username || user?.username}"`
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  const getCharacterImageUrl = (char) => {
    const clientId = accountInfo?.id || user?.id
    if (!clientId || !char.name || !char.world) return null
    // Formato: name_world.png (exemplo: lestimsa_2.png)
    const imageName = `${char.name.toLowerCase().replace(/\s+/g, '_')}_${char.world.toLowerCase().replace(/\s+/g, '_')}`
    return API.Images.getUrl(clientId, imageName)
  }

  const licenseStatus = getLicenseStatus()

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Account"
      >
      <div className="account">
        {/* Avatar Section */}
        <div className="account__header">
          <div className="account__header-top">
            <button className="account__leave-btn" onClick={handleLogout} title="Leave">
              <LogoutIcon />
              <span>Leave</span>
            </button>
          </div>
          <div className="account__header-body">
          <div className="account__avatar">
            <KnightAvatarIcon />
          </div>
          <div className="account__name">
            {editingNickname ? (
              <form className="account__nickname-inline-form" onSubmit={handleNicknameSubmit}>
                <input
                  type="text"
                  name="nickname"
                  value={nicknameForm.nickname}
                  onChange={handleNicknameChange}
                  placeholder={user.username}
                  maxLength="30"
                  disabled={nicknameLoading}
                  autoFocus
                  className={`account__nickname-inline-input${
                    nicknameError && nicknameForm.nickname.trim()
                      ? ' account__nickname-inline-input--error'
                      : nicknameForm.nickname.trim() && !nicknameError
                        ? ' account__nickname-inline-input--valid'
                        : ''
                  }`}
                />
                <span className="account__nickname-counter">
                  {nicknameForm.nickname.length}/30
                </span>
                <div className="account__nickname-inline-actions">
                  <button 
                    type="submit" 
                    className="account__nickname-inline-btn account__nickname-inline-btn--save"
                    disabled={nicknameLoading || !!nicknameError}
                    title="Save"
                  >
                    {nicknameLoading ? (
                      <div className="account__spinner-tiny"></div>
                    ) : (
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M9 12 L11 14 L15 10"/>
                      </svg>
                    )}
                  </button>
                  <button 
                    type="button"
                    className="account__nickname-inline-btn account__nickname-inline-btn--cancel"
                    onClick={() => {
                      setEditingNickname(false)
                      setNicknameForm({ nickname: user.nickname || '' })
                      setNicknameError('')
                      setNicknameSuccess('')
                    }}
                    disabled={nicknameLoading}
                    title="Cancel"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </form>
            ) : (
              <div className="account__username-container">
                <h3 className="account__username">
                  {user.username}
                  {user.nickname && (
                    <span className="account__nickname">aka "{user.nickname}"</span>
                  )}
                </h3>
                {!user.nickname && (
                  <span className="account__nickname account__nickname--empty" onClick={() => setEditingNickname(true)}>
                    + Set nickname
                  </span>
                )}
                {user.nickname && (
                <button 
                  className="account__username-edit-btn"
                  onClick={() => setEditingNickname(true)}
                  title="Edit nickname"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </button>
                )}
              </div>
            )}
            <div className="account__badges-row">
              <span className={`account__badge account__badge--${licenseStatus.class}`}>
                {loading ? '...' : `⚔️ ${licenseStatus.text}`}
              </span>
              {user.isAdmin && (
                <span className="account__badge account__badge--admin">👑 Admin</span>
              )}
              {accountInfo && (
                <span className={`account__badge account__badge--status-account account__badge--${getAccountStatus().class}`}>
                  {getAccountStatus().text === 'Active' ? '✅' : getAccountStatus().text === 'Suspended' ? '⏸️' : '🚫'} {getAccountStatus().text}
                </span>
              )}
              {onlineStatus && (
                <>
                  <span className={`account__badge account__badge--status account__badge--status-${onlineStatus.isAppOnline ? 'online' : 'offline'}`}
                        title="APP (Unity-BOT)">
                    <span className="account__status-dot"></span>
                    APP: {onlineStatus.isAppOnline ? 'Online' : 'Offline'}
                  </span>
                  <span className={`account__badge account__badge--status account__badge--status-${onlineStatus.isSiteOnline ? 'online' : 'offline'}`}
                        title="Site (Website)">
                    <span className="account__status-dot"></span>
                    Site: {onlineStatus.isSiteOnline ? 'Online' : 'Offline'}
                  </span>
                </>
              )}
            </div>
            {(nicknameError || nicknameSuccess) && (
              <div className="account__nickname-feedback">
                {nicknameError && <span className="account__nickname-error">⚠️ {nicknameError}</span>}
                {nicknameSuccess && <span className="account__nickname-success">✅ {nicknameSuccess}</span>}
              </div>
            )}
          </div>
          </div>
          <div className="account__last-update" aria-hidden>
            {isRefreshing ? 'Updating…' : (lastRefreshTime ? `Updated ${formatTimeSince(lastRefreshTime)}` : '')}
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="account__tabs">
          <button 
            className={`account__tab ${activeTab === 'info' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('info')}
          >
            <LicenseIcon /> Info
          </button>
          <button 
            className={`account__tab ${activeTab === 'characters' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('characters')}
          >
            <CharacterIcon /> Characters
            {characters.length > 0 && (
              <span className="account__tab-count">{characters.length}</span>
            )}
          </button>
          <button 
            className={`account__tab ${activeTab === 'security' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('security')}
          >
            <LockIcon /> Security
          </button>
          <button 
            className={`account__tab ${activeTab === 'buy' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('buy')}
          >
            <TabIcon /> Buy Tabs
          </button>
          <button 
            className={`account__tab ${activeTab === 'bot' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('bot')}
          >
            <BotTabIcon /> Unity-BOT
          </button>
          <button 
            className={`account__tab ${activeTab === 'config' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('config')}
          >
            <GearsIcon /> Bot Config
          </button>
          <button 
            className={`account__tab ${activeTab === 'support' ? 'account__tab--active' : ''}`}
            onClick={() => handleTabChange('support')}
          >
            <SupportIcon /> Support
          </button>
          
          {/* Refresh button moved to top-right corner for clearer placement */}
        </div>
        

        {/* Content Area */}
        {loading ? (
          <div className="account__content-skeleton">
            {activeTab === 'info' && <AccountInfoSkeleton />}
            {activeTab === 'characters' && <CharacterListSkeleton count={3} />}
            {activeTab === 'security' && <AccountInfoSkeleton />}
          </div>
        ) : error ? (
          <div className="account__error">
            <span>⚠️ {typeof error === 'object' ? error?.message || 'An error occurred' : error}</span>
            <button className="account__retry-btn" onClick={() => fetchAllData(false)}>
              <RefreshIcon /> Retry
            </button>
          </div>
        ) : (
          <div className="account__content-loaded">
            {/* Info Tab */}
            {activeTab === 'info' && (
              <div className="account__info">
                {/* Email */}
                <div className="account__info-item">
                  <EmailIcon />
                  <span className="account__info-label">Email:</span>
                  <span className="account__info-value">{accountInfo?.email || user.email || '—'}</span>
                </div>

                {/* Account Status */}
                <div className="account__info-item">
                  <ShieldIcon />
                  <span className="account__info-label">Account Status:</span>
                  <span className={`account__info-value ${getAccountStatus().class === 'active' ? 'account__info-value--active' : 'account__info-value--inactive'}`}>
                    {getAccountStatus().text}
                  </span>
                </div>

                {accountInfo && !accountInfo.isActive && accountInfo.inactiveReason && (
                  <div className="account__info-item account__info-item--sub">
                    <span className="account__info-label">└ Reason:</span>
                    <span className="account__info-value">{accountInfo.inactiveReason}</span>
                  </div>
                )}

                {accountInfo && !accountInfo.isActive && accountInfo.inactiveUntil && (
                  <div className="account__info-item account__info-item--sub">
                    <span className="account__info-label">└ Until:</span>
                    <span className="account__info-value">{formatDate(accountInfo.inactiveUntil)}</span>
                  </div>
                )}

                {accountInfo && accountInfo.deactivatedByAdmin && (
                  <div className="account__info-item account__info-item--sub">
                    <span className="account__info-label">└ Banned by:</span>
                    <span className="account__info-value account__info-value--admin">{accountInfo.deactivatedByAdmin}</span>
                  </div>
                )}

                <div className="account__info-divider" />

                {/* Email Verified */}
                <div className="account__info-item">
                  <EmailIcon />
                  <span className="account__info-label">Email Verified:</span>
                  <span className={`account__info-value ${accountInfo?.isEmailVerified ? 'account__info-value--active' : 'account__info-value--inactive'}`}>
                    {accountInfo?.isEmailVerified ? '✅ Yes' : '❌ No'}
                  </span>
                </div>

                {accountInfo && !accountInfo.isEmailVerified && (
                  <div className="account__verification-action">
                    <button
                      className="account__verify-btn"
                      onClick={handleResendVerification}
                      disabled={verifLoading}
                    >
                      {verifLoading && <div className="account__spinner-tiny" />}
                      {verifLoading ? 'Sending...' : 'Resend verification email'}
                    </button>
                    {verifMessage && (
                      <span className={`account__verify-message account__verify-message--${verifMessageType}`}>
                        {verifMessage}
                      </span>
                    )}
                  </div>
                )}

                {/* Available Tabs */}
                <div className="account__info-item">
                  <TabIcon />
                  <span className="account__info-label">Available Tabs:</span>
                  <span className="account__info-value account__info-value--highlight">
                    {getTotalTabs()}
                    {(license?.summary?.totalAvailableNewtabs || 0) > 0 && (
                      <span className="account__temp-badge">+{license.summary?.totalAvailableNewtabs || license.totalActiveTempTabs || 0} temp</span>
                    )}
                  </span>
                </div>

                {/* Permanent Tabs */}
                {license && (license.summary?.permanentTabs || license.totalPermanentTabs || license.tabs || 0) > 0 && (
                  <div className="account__info-item account__info-item--sub">
                    <span className="account__info-label">└ Permanent:</span>
                    <span className="account__info-value">{license.summary?.permanentTabs || license.totalPermanentTabs || license.tabs || 0}</span>
                  </div>
                )}


                {/* NewTab Packages */}
                {license?.newtabs && license.newtabs.length > 0 && (
                  <div className="account__packages">
                    <div className="account__packages-title">📦 Active Packages:</div>
                    {license.newtabs
                      .filter(nt => !nt.isExpired)
                      .map((nt, index) => (
                        <div key={nt.id || index} className="account__package">
                          <span className="account__package-qty">{nt.quantity} tabs</span>
                          <span className="account__package-exp">expires {formatShortDate(nt.expiresAt)}</span>
                          {nt.description && (
                            <span className="account__package-desc">{nt.description}</span>
                          )}
                          {nt.lastModifiedByAdminName && (
                            <span className="account__package-admin" title="Added by admin">
                              👤 {nt.lastModifiedByAdminName}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Character Count */}
                {characters.length > 0 && (
                  <div className="account__info-item">
                    <CharacterIcon />
                    <span className="account__info-label">Characters:</span>
                    <span className="account__info-value">{characters.length} ({characters.filter(c => c.online).length} online)</span>
                  </div>
                )}

                {/* Member Since */}
                <div className="account__info-item">
                  <CalendarIcon />
                  <span className="account__info-label">Member Since:</span>
                  <span className="account__info-value">
                    <span className="account__pill account__pill--member">
                      {formatMemberDuration(accountInfo?.createdAt || user.createdAt)}
                    </span>
                    <span className="account__info-subdate">{formatShortDate(accountInfo?.createdAt || user.createdAt)}</span>
                  </span>
                </div>

                {/* Last Login */}
                <div className="account__info-item">
                  <CalendarIcon />
                  <span className="account__info-label">Last Login:</span>
                  <span className="account__info-value">
                    <span className="account__pill account__pill--login">
                      {formatTimeAgo(accountInfo?.lastLoginAt || user.lastLoginAt)}
                    </span>
                    <span className="account__info-subdate">{formatDate(accountInfo?.lastLoginAt || user.lastLoginAt)}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Characters Tab */}
            {activeTab === 'characters' && (
              <div className="account__characters">
                {loadingCharacter && (
                  <LoadingOverlay label="Loading characters">
                    <div className="account__loading-skeleton">
                      <SkeletonAvatar size="60px" />
                      <div className="account__loading-skeleton-text">
                        <Skeleton width="150px" height="18px" />
                        <Skeleton width="100px" height="14px" style={{ marginTop: '8px' }} />
                      </div>
                    </div>
                  </LoadingOverlay>
                )}
                {characters.length === 0 ? (
                  <div className="account__no-chars">
                    <CharacterIcon />
                    <p>No characters found</p>
                    <span>Characters will appear here when created in-game</span>
                  </div>
                ) : (
                  <div className="account__char-list">
                    {characters.map((char, index) => {
                      const clientId = accountInfo?.id || user?.id
                      const imageName = char.name && char.world 
                        ? `${char.name.toLowerCase().replace(/\s+/g, '_')}_${char.world.toLowerCase().replace(/\s+/g, '_')}`
                        : null
                      const imageKey = `${char.id || index}_${imageName}`
                      const imageLoaded = loadedImages[imageKey]
                      
                      return (
                        <div 
                          key={char.id || index} 
                          className="account__char-card account__char-card--clickable"
                          onClick={() => handleCharacterClick(char)}
                          title="Click to view details"
                        >
                          <div className="account__char-avatar">
                                  {clientId && imageName ? (
                                    <AuthImage
                                      clientId={clientId}
                                      filename={imageName}
                                      alt={char.name}
                                      className="account__char-avatar-img"
                                      onLoad={() => setLoadedImages(prev => ({ ...prev, [imageKey]: true }))}
                                      onError={() => setLoadedImages(prev => ({ ...prev, [imageKey]: false }))}
                                    />
                                  ) : (
                                    <div className="account__char-fallback">
                                      <SkeletonAvatar size="48px" />
                                    </div>
                                  )}
                                  {/* If an image was attempted but failed, show neutral placeholder rather than default icon */}
                                  {clientId && imageName && imageLoaded === false && (
                                    <div className="account__char-fallback">
                                      <SkeletonAvatar size="48px" />
                                    </div>
                                  )}
                          </div>
                          <div className="account__char-info">
                            <div className="account__char-header">
                              <span className="account__char-name">{char.name}</span>
                              <span className={`account__char-status account__char-status--${char.online ? 'online' : 'offline'}`}>
                                <span className="account__char-status-dot"></span>
                                {char.online ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            <div className="account__char-details">
                              <span className="account__char-world">
                                <WorldIcon /> {char.world || 'Unknown'}
                              </span>
                            {(char.level || char.gameData?.level) && (
                              <span className="account__char-level">
                                <LevelIcon /> Level {char.level || char.gameData?.level}
                              </span>
                            )}
                          </div>
                          {(char.gameData?.vocation || char.vocation) && (
                            <div className="account__char-vocation">
                              {char.gameData?.vocation || char.vocation}
                            </div>
                          )}
                        </div>
                        <div className="account__char-arrow">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    )})}
                  </div>
                )}
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="account__security">
                <form className="account__password-form" onSubmit={handlePasswordSubmit} >
                  <div className="account__security-header">
                    <LockIcon />
                    <h4>Change Password</h4>
                  </div>
                  <div className="account__password-fields-grid">
                    {/* Current Password */}
                    <div className="account__form-field">
                      <label htmlFor="currentPassword">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path d="M8 11 L8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7 L16 11" fill="none" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Current Password
                      </label>
                      <input
                        type="text"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="••••••••"
                        disabled={passwordLoading}
                        required
                        autoComplete='off'
                      />
                    </div>

                    {/* New Password */}
                    <div className="account__form-field">
                      <label htmlFor="newPassword">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path d="M8 11 L8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7 L16 11" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
                          <path d="M12 14 L12 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="••••••••"
                        disabled={passwordLoading}
                        required
                      />
                    </div>

                    {/* Confirm Password */}
                    <div className="account__form-field">
                      <label htmlFor="confirmPassword">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path d="M8 11 L8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7 L16 11" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path d="M9 15 L12 18 L15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="••••••••"
                        disabled={passwordLoading}
                        required
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {passwordError && (
                    <div className="account__password-error">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 8 L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
                      </svg>
                      <span>{passwordError}</span>
                    </div>
                  )}

                  {/* Success Message */}
                  {passwordSuccess && (
                    <div className="account__password-success">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <path d="M9 12 L11 14 L15 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{passwordSuccess}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="account__password-submit"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? (
                      <>
                        <div className="account__spinner-small"></div>
                        <span>Changing...</span>
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="5" y="11" width="14" height="10" rx="2"/>
                          <path d="M8 11 L8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7 L16 11"/>
                        </svg>
                        <span>Change Password</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Buy Tabs Tab */}
            {activeTab === 'buy' && (
              <div className="account__buy-tabs">
                {!showPixPayment ? (
                  <>
                {/* New 2-column layout */}
                <div className="account__buy-grid">
                  {/* Left column - Selectors */}
                  <div className="account__selectors-card">
                    <div className="account__card-header">
                      <TabIcon />
                      <h5>Configure seu Pacote</h5>
                    </div>
                    
                    <div className="account__selector-group">
                      <label className="account__selector-label">
                        <span className="account__label-icon"><TabIcon /></span>
                        <span className="account__label-text">Quantidade de Tabs</span>
                      </label>
                      <div className="account__slider-wrapper">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={selectedTabs}
                          onChange={(e) => handleTabsChange(e.target.value)}
                          className="account__slider"
                          style={{
                            '--slider-progress': `${calculateSliderProgress(selectedTabs, 1, 10)}%`
                          }}
                        />
                        <div className="account__slider-value-badge">{selectedTabs}</div>
                      </div>
                      <div className="account__slider-labels">
                        <span>1</span>
                        <span>5</span>
                        <span>10</span>
                      </div>
                    </div>

                    <div className="account__selector-divider"></div>

                    <div className="account__selector-group">
                      <label className="account__selector-label">
                        <span className="account__label-icon"><CalendarIcon /></span>
                        <span className="account__label-text">Duração (Meses)</span>
                      </label>
                      <div className="account__slider-wrapper">
                        <input
                          type="range"
                          min="1"
                          max="6"
                          value={selectedMonths}
                          onChange={(e) => handleMonthsChange(e.target.value)}
                          className="account__slider"
                          style={{
                            '--slider-progress': `${calculateSliderProgress(selectedMonths, 1, 6)}%`
                          }}
                        />
                        <div className="account__slider-value-badge">{selectedMonths}</div>
                      </div>
                      <div className="account__slider-labels">
                        <span>1</span>
                        <span>3</span>
                        <span>6</span>
                      </div>
                    </div>
                  </div>

                  {/* Right column - Price Summary Card */}
                  <div className="account__price-card">
                    <div className="account__card-header">
                      <CoinIcon />
                      <h5>Resumo do Pedido</h5>
                    </div>
                    
                    <div className="account__order-summary">
                      <div className="account__order-item">
                        <span className="account__order-label">Pacote</span>
                        <span className="account__order-value">{selectedTabs} tab{selectedTabs > 1 ? 's' : ''} × {selectedMonths} mês{selectedMonths > 1 ? 'es' : ''}</span>
                      </div>
                      
                      {(() => {
                        const base = selectedTabs * selectedMonths * TAB_PRICE
                        const discounts = getDiscountRate(selectedTabs, selectedMonths)
                        const combinedRate = discounts?.combined || 0
                        const combinedAmount = Math.round(base * combinedRate * 100) / 100
                        const total = calculatePrice(selectedTabs, selectedMonths)

                        return (
                          <>
                            <div className="account__order-item">
                              <span className="account__order-label">Valor Base</span>
                              <span className="account__order-value">
                                R$ {base.toFixed(2)}
                                {combinedRate > 0 && (
                                  <span className="account__order-discount"> -R$ {combinedAmount.toFixed(2)}</span>
                                )}
                              </span>
                            </div>

                            <div className="account__total-divider"></div>
                            
                            <div className="account__order-total">
                              <span className="account__total-label">Total a Pagar</span>
                              <div className="account__total-price">
                                <span className="account__currency">R$</span>
                                <span className="account__total-amount">{total.toFixed(2)}</span>
                              </div>
                            </div>
                          </>
                        )
                      })()}
                    </div>

                    <div className="account__savings-tip">
                      <ScrollIcon />
                      <span>Dica: Mais meses = Mais economia!</span>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {purchaseError && (
                  <div className="account__purchase-error">
                    ⚠️ {purchaseError}
                  </div>
                )}

                {/* Success Message */}
                {purchaseSuccess && (
                  <div className="account__purchase-success">
                    ✅ {purchaseSuccess}
                  </div>
                )}

                {/* Purchase Button */}
                <button 
                  className="account__purchase-btn"
                  onClick={handlePurchase}
                  disabled={purchaseLoading}
                >
                  {purchaseLoading ? (
                    <>
                      <div className="account__spinner-small"></div>
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <CoinIcon />
                      <span>Comprar por R${calculatePrice(selectedTabs, selectedMonths).toFixed(2)}</span>
                      <CoinIcon />
                    </>
                  )}
                </button>
                </>
                ) : (
                  /* PIX Payment Section */
                  <div className="account__pix-payment">
                    <div className="account__pix-header">
                    </div>
                    
                    {/* QR Code Section */}
                    <div className="account__qr-section">
                      <div className="account__qr-container">
                        <QRCodeSVG 
                          value={pixPayload || generatePixPayload(selectedPackage?.price || 0)}
                          size={220}
                          level="M"
                          includeMargin={true}
                          className="account__qr-code"
                        />
                      </div>
                      <p className="account__qr-description">Escaneie o código QR ou copie o código abaixo</p>
                      <div className="account__qr-info">
                        <div className="account__qr-amount">
                          <CoinIcon />
                          <div className="account__qr-amount-text">
                            <span className="account__qr-label">Valor:</span>
                            <span className="account__qr-value">R$ {selectedPackage?.price.toFixed(2)}</span>
                          </div>
                          <CoinIcon />
                        </div>
                        <div className="account__qr-package">
                          <TabIcon /> <strong>{selectedPackage?.tabs}</strong> tab{selectedPackage?.tabs > 1 ? 's' : ''} por <strong>{selectedPackage?.months}</strong> mês{selectedPackage?.months > 1 ? 'es' : ''}
                        </div>
                      </div>
                    </div>
                    
                    <div className="account__pix-key-section">
                      <div className="account__pix-key-header">
                        <span className="account__pix-key-label">Código PIX (Copia e Cola)</span>
                        <span className="account__pix-bank">Com Valor</span>
                      </div>
                      <div className="account__pix-key-container">
                        <code className="account__pix-key">{pixPayload || generatePixPayload(selectedPackage?.price || 0)}</code>
                        <button 
                          className={`account__pix-copy-btn ${pixKeyCopied ? 'account__pix-copy-btn--copied' : ''}`}
                          onClick={copyPixKey}
                          title="Copiar código PIX completo"
                        >
                          {pixKeyCopied ? <CheckIcon /> : <CopyIcon />}
                          <span>{pixKeyCopied ? 'Copiado!' : 'Copiar'}</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="account__pix-instructions">
                      <h5><ScrollIcon /> Como Pagar</h5>
                      <ol>
                        <li>Abra o aplicativo do seu banco</li>
                        <li>Escolha a opção <strong>PIX → Copia e Cola</strong></li>
                        <li>Cole o código PIX copiado acima</li>
                        <li>Confira o valor de <strong>R${selectedPackage?.price.toFixed(2)}</strong></li>
                        <li>Finalize o pagamento</li>
                        <li>Entre em contato com o suporte para confirmar</li>
                      </ol>
                    </div>
                    
                    <div className="account__pix-support">
                      <h5><ScrollIcon /> Confirmação de Pagamento</h5>
                      <p>Após realizar o pagamento, entre em contato para ativar suas tabs:</p>
                      <div className="account__support-buttons">
                        <button className="account__support-btn account__support-btn--whatsapp" onClick={openWhatsApp}>
                          <WhatsAppIcon />
                          <div className="account__support-info">
                            <span className="account__support-label">WhatsApp</span>
                            <span className="account__support-value">{SUPPORT_WHATSAPP_DISPLAY}</span>
                          </div>
                        </button>
                        <button className="account__support-btn account__support-btn--email" onClick={openEmail}>
                          <EmailSupportIcon />
                          <div className="account__support-info">
                            <span className="account__support-label">Email</span>
                            <span className="account__support-value">{SUPPORT_EMAIL}</span>
                          </div>
                        </button>
                      </div>
                    </div>
                    
                    <button 
                      className="account__pix-back-btn"
                      onClick={() => {
                        setShowPixPayment(false)
                        setPixKeyCopied(false)
                        setPixPayload('')
                      }}
                    >
                      ← Voltar aos Pacotes
                    </button>
                  </div>
                )}

                <div className="account__buy-footer">
                  <p>🔒 <strong>Pagamento Seguro:</strong> Utilize PIX para transferência instantânea e segura.</p>
                  <p>⚡ <strong>Ativação Rápida:</strong> Suas tabs serão ativadas em até 24 horas após confirmação.</p>
                  <p>💬 <strong>Suporte:</strong> Estamos aqui para ajudar via WhatsApp ou Email.</p>
                </div>
              </div>
            )}

            {/* Support Tab */}
            {/* Unity-BOT Tab */}
            {activeTab === 'bot' && (
              <div className="account__bot">
                {botLoading ? (
                  <div className="account__content-skeleton">
                    <Skeleton width="100%" height="70px" borderRadius="10px" />
                    <Skeleton width="100%" height="40px" borderRadius="8px" />
                    <Skeleton width="100%" height="120px" borderRadius="10px" />
                  </div>
                ) : (
                  <>
                    {/* Bot Status Card - Enhanced */}
                    <div className="account__bot-status-card">
                      <div className="account__bot-icon-wrapper">
                        <img src="/assets/logo-128x128.svg" alt="Unity-BOT" className="account__bot-logo-img" />
                        <div className="account__bot-icon-glow"></div>
                      </div>
                      <div className="account__bot-status-info">
                        <div className="account__bot-header">
                          <span className="account__bot-title">Unity-BOT</span>
                          <span className="account__bot-badge account__bot-badge--ok">
                            <svg viewBox="0 0 16 16" width="12" height="12">
                              <circle cx="8" cy="8" r="6" fill="#22c55e"/>
                              <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Online
                          </span>
                        </div>
                        <div className="account__bot-version-row">
                          <span className="account__bot-version">
                            Version {botStatus?.version || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Bot Info Grid */}
                    <div className="account__bot-info-grid">
                      <div className="account__bot-info-item">
                        <span className="account__bot-info-label">Installer Available</span>
                        <span className="account__bot-info-value">{botStatus?.hasInstaller ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="account__bot-info-item">
                        <span className="account__bot-info-label">Size</span>
                        <span className="account__bot-info-value">{botStatus?.installerSizeFormatted || 'N/A'}</span>
                      </div>
                      <div className="account__bot-info-item">
                        <span className="account__bot-info-label">Published</span>
                        <span className="account__bot-info-value">
                          {botStatus?.publishedAt 
                            ? new Date(botStatus.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>

                    {/* Download Buttons Row */}
                    <div className="account__bot-downloads">
                      <button
                        onClick={handleDownloadBot}
                        disabled={botDownloading}
                        className="account__bot-update-btn"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16">
                          <defs>
                            <linearGradient id="updIcon" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#fbbf24"/>
                              <stop offset="100%" stopColor="#d97706"/>
                            </linearGradient>
                          </defs>
                          <path d="M21 12a9 9 0 11-2.6-6.3" stroke="url(#updIcon)" strokeWidth="2.2" strokeLinecap="round" fill="none"/>
                          <path d="M21 3v6h-6" stroke="url(#updIcon)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          <circle cx="21" cy="3" r="1.5" fill="#fbbf24"/>
                        </svg>
                        <span>{botDownloading ? 'Downloading...' : 'Full Installer'}</span>
                        <span className="account__bot-update-badge">EXE</span>
                      </button>

                      <button
                        onClick={handleDownloadPatch}
                        disabled={downloadingPatch}
                        className="account__bot-patch-btn"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16">
                          <defs>
                            <linearGradient id="patchIcon" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#22c55e"/>
                              <stop offset="100%" stopColor="#16a34a"/>
                            </linearGradient>
                          </defs>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" 
                                stroke="url(#patchIcon)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                        </svg>
                        <span>{downloadingPatch ? 'Downloading...' : 'Update Patch'}</span>
                        <span className="account__bot-update-badge">ZIP</span>
                      </button>
                    </div>

                    {/* Changelog - Enhanced */}
                    {botChangelog.length > 0 && (
                      <div className="account__bot-changelog">
                        <h5 className="account__bot-changelog-title">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <defs>
                              <linearGradient id="clAccGr" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#fde68a"/>
                                <stop offset="50%" stopColor="#fbbf24"/>
                                <stop offset="100%" stopColor="#f59e0b"/>
                              </linearGradient>
                            </defs>
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="url(#clAccGr)" stroke="#92400e" strokeWidth="1"/>
                            <path d="M14 2v6h6" fill="none" stroke="#92400e" strokeWidth="1"/>
                            <path d="M8 13h8M8 17h6" stroke="#78350f" strokeWidth="1.5" strokeLinecap="round" opacity="0.9"/>
                          </svg>
                          <span>Recent Updates</span>
                          <span className="account__bot-changelog-count">{botChangelog.length}</span>
                        </h5>
                        <div className="account__bot-changelog-list">
                          {(showAllChangelogs ? botChangelog : botChangelog.slice(0, 1)).map((entry, index) => (
                            <div key={entry.version} className={`account__bot-changelog-entry ${index === 0 ? 'account__bot-changelog-entry--latest' : ''}`}>
                              <div className="account__bot-changelog-header">
                                <span className="account__bot-changelog-ver">
                                  {index === 0 && <span className="account__bot-changelog-new-badge">NEW</span>}
                                  v{entry.version}
                                </span>
                                <span className="account__bot-changelog-date">
                                  {entry.date ? new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                </span>
                              </div>
                              <ul className="account__bot-changelog-changes">
                                {(entry.changes || []).map((c, i) => (
                                  <li key={i}>{c}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        {botChangelog.length > 1 && (
                          <button 
                            className="account__bot-changelog-toggle"
                            onClick={() => setShowAllChangelogs(!showAllChangelogs)}
                          >
                            {showAllChangelogs ? (
                              <>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 15l-6-6-6 6" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Show Less
                              </>
                            ) : (
                              <>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Show All Updates ({botChangelog.length - 1} more)
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Bot Config Tab */}
            {activeTab === 'config' && (
              <div className="account__config-tab">
                {configLoading && !telegramConfigs.length && (
                  <div className="account__content-skeleton">
                    <Skeleton width="100%" height="40px" borderRadius="8px" />
                    <Skeleton width="100%" height="60px" borderRadius="8px" style={{marginTop:8}} />
                  </div>
                )}
                {configError && (
                  <div className="account__password-error" style={{marginBottom:8}}>⚠️ {configError}</div>
                )}

                {/* Config Sub-tabs */}
                <div className="account__config-subtabs">
                  {['telegram', 'binds', 'unreal', 'life'].map(st => (
                    <button key={st}
                      className={`account__config-subtab ${configSubTab === st ? 'account__config-subtab--active' : ''}`}
                      onClick={() => setConfigSubTab(st)}
                    >
                      {st === 'telegram' ? (
                        <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>Telegram</>
                      ) : st === 'binds' ? (
                        <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>Binds</>
                      ) : st === 'unreal' ? (
                        <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>Unreal</>
                      ) : (
                        <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>Life Ins.</>
                      )}
                    </button>
                  ))}
                </div>

                {/* Telegram Configs */}
                {configSubTab === 'telegram' && (
                  <div className="account__config-section">
                    <div className="account__config-list">
                      <h5>Saved Telegram Configs ({telegramConfigs.length})</h5>
                      {telegramConfigs.length === 0 ? <p className="account__config-empty">No telegram configs</p> : (
                        telegramConfigs.map((cfg, i) => (
                          <div key={cfg.id || i} className="account__config-item">
                            <div className="account__config-item-info">
                              <strong>Tab {cfg.tabName}</strong>
                              <pre className="account__config-json">{JSON.stringify(cfg.configData, null, 2)}</pre>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Bind Configs */}
                {configSubTab === 'binds' && (
                  <div className="account__config-section">
                    <div className="account__config-list">
                      <h5>Saved Binds ({bindConfigs.length})</h5>
                      {bindConfigs.length === 0 ? <p className="account__config-empty">No bind configs</p> : (
                        bindConfigs.map((b, i) => (
                          <div key={b.id || i} className="account__config-item">
                            <div className="account__config-item-info">
                              <strong>{b.name}@{b.world}</strong>
                              <span>[{b.type}] {b.path}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Unreal Credentials */}
                {configSubTab === 'unreal' && (
                  <div className="account__config-section">
                    <div className="account__config-list">
                      <h5>Saved Credentials ({unrealCredentials.length})</h5>
                      {unrealCredentials.length === 0 ? <p className="account__config-empty">No credentials</p> : (
                        unrealCredentials.map((c, i) => (
                          <div key={c.id || i} className="account__config-item">
                            <div className="account__config-item-info">
                              <strong>{c.name}@{c.world}</strong>
                              <span>Password saved</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Life Insurance */}
                {configSubTab === 'life' && (
                  <div className="account__config-section">
                    <div className="account__config-list">
                      <h5>Saved Preferences ({lifeInsurancePrefs.length})</h5>
                      {lifeInsurancePrefs.length === 0 ? <p className="account__config-empty">No preferences</p> : (
                        lifeInsurancePrefs.map((p, i) => (
                          <div key={p.id || i} className="account__config-item">
                            <div className="account__config-item-info">
                              <strong>{p.characterName}@{p.world}</strong>
                              <span>Value: {p.data}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Support Tab */}
            {activeTab === 'support' && (
              <div className="account__support">
                {supportLoading ? (
                  <div className="account__content-skeleton">
                    <AccountInfoSkeleton />
                  </div>
                ) : (
                  <>
                    <div className="account__support-section">
                      <div className="account__support-section-header">
                        <SupportIcon />
                        <h5>Contact Support</h5>
                      </div>
                      <div className="account__support-contacts">
                        <button className="account__support-btn account__support-btn--whatsapp" onClick={openWhatsApp}>
                          <WhatsAppIcon />
                          <div className="account__support-info">
                            <span className="account__support-label">WhatsApp</span>
                            <span className="account__support-value">{SUPPORT_WHATSAPP_DISPLAY}</span>
                          </div>
                        </button>
                        <button className="account__support-btn account__support-btn--email" onClick={openEmail}>
                          <EmailSupportIcon />
                          <div className="account__support-info">
                            <span className="account__support-label">Email</span>
                            <span className="account__support-value">{SUPPORT_EMAIL}</span>
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className="account__support-section">
                      <div className="account__support-section-header">
                        <SupportIcon />
                        <h5>Server Status</h5>
                      </div>
                      <div className="account__support-server">
                        {serverStatus ? (
                          <>
                            <div className="account__support-server-header">
                              <span className={`account__support-server-dot account__support-server-dot--${serverStatus.status || 'unknown'}`} />
                              <span className="account__support-server-status">
                                {(serverStatus.status || 'unknown').toUpperCase()}
                              </span>
                            </div>
                            {serverStatus.message && (
                              <p className="account__support-server-msg">{serverStatus.message}</p>
                            )}
                            {serverStatus.estimatedRecoveryAt && (
                              <p className="account__support-server-recovery">
                                Estimated recovery: {formatDate(serverStatus.estimatedRecoveryAt)}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="account__support-server-loading">Loading server status...</p>
                        )}
                      </div>
                    </div>
                    <div className="account__support-section">
                      <div className="account__support-section-header">
                        <svg viewBox="0 0 24 24" width="18" height="18" className="account__info-icon">
                          <defs>
                            <linearGradient id="faqGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#e6c84a" />
                              <stop offset="100%" stopColor="#8b7355" />
                            </linearGradient>
                          </defs>
                          <circle cx="12" cy="12" r="9" fill="none" stroke="url(#faqGrad)" strokeWidth="2"/>
                          <path d="M12 17h.01M9.09 10a3 3 0 015.83 1c0 2-3 3-3 3" stroke="url(#faqGrad)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <h5>Quick FAQ</h5>
                      </div>
                      <div className="account__support-faq">
                        <div className="account__faq-item">
                          <details>
                            <summary>How do I activate my tabs after purchase?</summary>
                            <p>Contact us via WhatsApp or Email with your payment receipt. Tabs are usually activated within 24 hours.</p>
                          </details>
                        </div>
                        <div className="account__faq-item">
                          <details>
                            <summary>Where can I download the bot?</summary>
                            <p>Go to the <strong>Unity-BOT</strong> tab in this panel to download the full installer or update patch.</p>
                          </details>
                        </div>
                        <div className="account__faq-item">
                          <details>
                            <summary>How do I get a refund?</summary>
                            <p>Contact our support team via WhatsApp or Email. Refunds are processed on a case-by-case basis.</p>
                          </details>
                        </div>
                        <div className="account__faq-item">
                          <details>
                            <summary>Why was my account suspended?</summary>
                            <p>Check the Info tab for details about your account status. If you believe this is a mistake, contact support.</p>
                          </details>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        )}

      </div>
    </Modal>
      {/* Character Detail Modal */}
      <CharacterModal 
        isOpen={characterModalOpen}
        onClose={closeCharacterModal}
        characterName={selectedCharacter?.name}
        characterWorld={selectedCharacter?.world}
        characterData={selectedCharacter}
        onDelete={handleDeleteCharacter}
        user={user}
      />
    </>
  )
}

export default AccountModal
